import { YStack } from 'tamagui';
import { AppTopBar } from './AppTopBar';

/**
 * Outer wrapper for canonical screens. Caps the inner column at
 * 1440px on `xl` viewports so ultra-wide desktop windows don't
 * stretch hero images and content full-bleed forever, while
 * keeping the page background ($surfaceCanvas) tiled behind the
 * column on either side.
 *
 * Also renders the global `<AppTopBar>` floating on top of the
 * column (low-opacity background, hero photos visible through it).
 *
 * Two top-bar modes:
 *
 *   1. Non-hero pages — use `showBack` / `rightSlot` to configure
 *      the page-level bar. The bar overlays the scroll surface;
 *      content should leave room for it with paddingTop (use the
 *      `useAppTopBarHeight()` helper).
 *
 *   2. Hero pages — pass `showTopBar={false}` and render a
 *      `<HeroSection>` (or `<HeroStrip>`) which embeds its own
 *      AppTopBar inside the hero image. Avoids the bar overlaying
 *      the wrong place.
 *
 * Usage (replaces the outer `<YStack flex={1} bg="$surfaceCanvas">`
 * that every screen used to have):
 *
 *   <ResponsivePage showBack rightSlot={<MyAction />}>
 *     <ScrollView>...</ScrollView>
 *   </ResponsivePage>
 *
 * Per ADR-0012 / ADR-0023: `xl` = ≥1280px. We pick the cap
 * slightly above the breakpoint so the layout grows a bit past
 * the breakpoint before it locks in.
 */
interface ResponsivePageProps {
  children: React.ReactNode;
  /** Set to false on screens that render their own bar via
   *  `<HeroSection>` / `<HeroStrip>` (those embed AppTopBar inside
   *  the hero image so the bar sits over the image, not the page). */
  showTopBar?: boolean | undefined;
  /** Render the back arrow on the page-level top bar. Ignored when
   *  `showTopBar={false}` — pass it to the hero component instead. */
  showBack?: boolean | undefined;
  /** Per-screen actions rendered in the top bar's right slot
   *  (between the kiips logo and the hamburger menu). Ignored when
   *  `showTopBar={false}`. */
  rightSlot?: React.ReactNode | undefined;
}

export function ResponsivePage({
  children,
  showTopBar = true,
  showBack,
  rightSlot,
}: ResponsivePageProps) {
  return (
    <YStack flex={1} backgroundColor="$surfaceCanvas">
      <YStack
        flex={1}
        width="100%"
        alignSelf="center"
        $xl={{ maxWidth: 1440 }}
      >
        {children}
      </YStack>
      {showTopBar ? <AppTopBar showBack={showBack} rightSlot={rightSlot} /> : null}
    </YStack>
  );
}
